import React from 'react';
import Button from './shared/Button';
import { generateCitationForDisplay, getPrimaryAuthor } from '../utils/citationDisplayHelper';

const CitationCard = ({
	citation,
	onEdit,
	onDelete,
	onInsert,
	onViewDetails,
	isSelected,
	onSelect
}) => {
	if (!citation) return null;

	const { sourceType, category } = citation;
	const primaryAuthor = getPrimaryAuthor(citation);
	const fullCitation = generateCitationForDisplay(citation);

	// Inline style for better word breaking of long URLs
	const textBreakStyle = {
		wordBreak: 'break-word',
		overflowWrap: 'anywhere'
	};

	const handleCardClick = () => {
		if (onSelect) {
			onSelect(citation);
		}
	};

	return (
		<div
			onClick={handleCardClick}
			className={`rounded-[10px] cursor-pointer transition-all ${
				isSelected ? 'p-[1px] bg-gradient-to-r from-orange to-purple' : 'border border-[#F0F0F0]'
			}`}
		>
			<div className={`bg-white rounded-[9px] ${isSelected ? 'p-4' : 'p-4'}`}>
				<span className="text-[#686576] text-10 font-medium leading-100 mb-[14px] block">
					{category} | {sourceType}
				</span>
				<div className="flex justify-between items-center mb-3">
					<div>
						<h3 className="text-sm leading-100 font-bold text-dark-blue">{primaryAuthor}</h3>
					</div>
					{isSelected && (
						<div className="flex space-x-2">
							<button
								onClick={(e) => {
									e.stopPropagation();
									onEdit(citation);
								}}
								className="text-purple-500 hover:text-purple-700 transition"
							>
								<svg
									width="16"
									height="17"
									viewBox="0 0 16 17"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<g opacity="0.4">
										<path
											d="M4.82867 12.594H2V9.7653L9.62333 2.14197C9.74835 2.01699 9.91789 1.94678 10.0947 1.94678C10.2714 1.94678 10.441 2.01699 10.566 2.14197L12.452 4.0273C12.514 4.08922 12.5632 4.16274 12.5967 4.24367C12.6303 4.32461 12.6475 4.41136 12.6475 4.49897C12.6475 4.58658 12.6303 4.67333 12.5967 4.75426C12.5632 4.83519 12.514 4.90872 12.452 4.97063L4.82867 12.594ZM2 13.9273H14V15.2606H2V13.9273Z"
											fill="#180A63"
										/>
									</g>
								</svg>
							</button>
							<button
								onClick={(e) => {
									e.stopPropagation();
									onDelete(citation);
								}}
								className="text-red-500 hover:text-red-700 transition"
							>
								<svg
									width="16"
									height="17"
									viewBox="0 0 16 17"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<g opacity="0.4">
										<path
											d="M12.967 5.5957H3.03366C2.98755 5.59556 2.94191 5.60498 2.89962 5.62337C2.85734 5.64176 2.81933 5.66873 2.788 5.70257C2.75668 5.7364 2.73271 5.77637 2.71762 5.81994C2.70252 5.86351 2.69664 5.90974 2.70033 5.9557L3.55366 15.3824C3.5837 15.7148 3.73733 16.0239 3.98419 16.2485C4.23106 16.4732 4.55321 16.5971 4.88699 16.5957H11.1137C11.4474 16.5971 11.7696 16.4732 12.0165 16.2485C12.2633 16.0239 12.417 15.7148 12.447 15.3824L13.3337 5.92904C13.3373 5.88307 13.3315 5.83684 13.3164 5.79327C13.3013 5.7497 13.2773 5.70973 13.246 5.6759C13.2147 5.64206 13.1766 5.6151 13.1344 5.5967C13.0921 5.57831 13.0464 5.56889 13.0003 5.56904L12.967 5.5957ZM6.83366 14.2624C6.83366 14.395 6.78098 14.5222 6.68721 14.6159C6.59344 14.7097 6.46627 14.7624 6.33366 14.7624C6.20105 14.7624 6.07387 14.7097 5.98011 14.6159C5.88634 14.5222 5.83366 14.395 5.83366 14.2624V8.26237C5.83366 8.12976 5.88634 8.00258 5.98011 7.90882C6.07387 7.81505 6.20105 7.76237 6.33366 7.76237C6.46627 7.76237 6.59344 7.81505 6.68721 7.90882C6.78098 8.00258 6.83366 8.12976 6.83366 8.26237V14.2624ZM10.167 14.2624C10.167 14.395 10.1143 14.5222 10.0205 14.6159C9.92678 14.7097 9.7996 14.7624 9.66699 14.7624C9.53438 14.7624 9.40721 14.7097 9.31344 14.6159C9.21967 14.5222 9.16699 14.395 9.16699 14.2624V8.26237C9.16699 8.12976 9.21967 8.00258 9.31344 7.90882C9.40721 7.81505 9.53438 7.76237 9.66699 7.76237C9.7996 7.76237 9.92678 7.81505 10.0205 7.90882C10.1143 8.00258 10.167 8.12976 10.167 8.26237V14.2624ZM14.667 3.26237H11.5003C11.4561 3.26237 11.4137 3.24481 11.3825 3.21355C11.3512 3.1823 11.3337 3.13991 11.3337 3.0957V2.26237C11.3337 1.82034 11.1581 1.39642 10.8455 1.08386C10.5329 0.771298 10.109 0.595703 9.66699 0.595703L6.33366 0.595703C5.89163 0.595703 5.46771 0.771298 5.15515 1.08386C4.84259 1.39642 4.66699 1.82034 4.66699 2.26237V3.0957C4.66699 3.13991 4.64943 3.1823 4.61818 3.21355C4.58692 3.24481 4.54453 3.26237 4.50033 3.26237H1.33366C1.15685 3.26237 0.987279 3.33261 0.862254 3.45763C0.73723 3.58266 0.666992 3.75223 0.666992 3.92904C0.666992 4.10585 0.73723 4.27542 0.862254 4.40044C0.987279 4.52547 1.15685 4.5957 1.33366 4.5957H14.667C14.8438 4.5957 15.0134 4.52547 15.1384 4.40044C15.2634 4.27542 15.3337 4.10585 15.3337 3.92904C15.3337 3.75223 15.2634 3.58266 15.1384 3.45763C15.0134 3.33261 14.8438 3.26237 14.667 3.26237ZM6.00033 3.0957V2.26237C6.00033 2.17396 6.03544 2.08918 6.09796 2.02667C6.16047 1.96416 6.24525 1.92904 6.33366 1.92904H9.66699C9.7554 1.92904 9.84018 1.96416 9.90269 2.02667C9.96521 2.08918 10.0003 2.17396 10.0003 2.26237V3.0957C10.0003 3.13991 9.98277 3.1823 9.95151 3.21355C9.92025 3.24481 9.87786 3.26237 9.83366 3.26237H6.16699C6.12279 3.26237 6.0804 3.24481 6.04914 3.21355C6.01789 3.1823 6.00033 3.13991 6.00033 3.0957Z"
											fill="#E90B0F"
										/>
									</g>
								</svg>
							</button>
						</div>
					)}
				</div>

				<div className="border-l-[1.5px] border-[#F0F0F0] pl-3 pr-2 mb-3">
					<p className="text-xs text-dark-blue leading-[18px] font-medium" style={textBreakStyle}>
						{fullCitation}
					</p>
				</div>

				{isSelected && (
					<>
						<button
							onClick={(e) => {
								e.stopPropagation();
								if (onViewDetails) onViewDetails(citation);
							}}
							className="text-dark-blue text-xs font-medium underline mb-4 block"
						>
							View details
						</button>

						<Button
							variant="outline-gradient-text"
							onClick={(e) => {
								e.stopPropagation();
								onInsert(citation);
							}}
							className="w-full border-gradient"
						>
							Insert citation
						</Button>
					</>
				)}
			</div>
		</div>
	);
};

export default CitationCard;
